import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { locationsKey } from '../../../solaris-lens-common-lib/src/constants/queryKeys/Locations';
import { INSTALLED_PRODUCTS_VIEW_SUMMARY } from '../../../solaris-lens-common-lib/src/constants/turbines';
import {
  getLocationsV3Serv,
  createLocations,
  updatelocationServ,
  getLocationIdServ,
  getLocationAccessLevelsServ,
  getLocationsWithAccessLevelsServ,
  getUnassignedTurbinesServ,
  getPowerSitesServ,
  deleteUserCompanyServ,
  getInstalledProductsBySiteServ,
  getDataPointsServ,
  updateLocationAccessLevelsServ,
  deletelocationServ
} from '../../../solaris-lens-common-lib/src/services/locations';
import { RootState } from '../../../store';
import useShouldRefresh from '@/hooks/useShouldRefresh';
import { getUTC } from '@/solaris-lens-common-lib/src/utils/dates';

export interface LocationFormData {
  id?: string;
  name: string;
  numberOfTurbines: string;
  address: string;
  city: string;
  customer: string;
  nearestCity: string;
  state: string;
  latitude: string;
  longitude: string;
  startDate: string;
  status: 'Active' | 'Inactive';
  dataPoints: Record<string, boolean>;
}
interface LocationApiPayload {
  name: string;
  address: string;
  city: string;
  state: string;
  geoReference: {
    latitude: string;
    longitude: string;
  };
  customer: string;
  type: string;
  startDate: string;
  status: string;
  isActive: boolean;
  category: string;
  dataPointVisibility?: string[];
}

interface LocationsResponse {
  status: string;
  data: any[];
  info: {
    pagination?: {
      currentPage: number;
      totalPages: number;
      nextPageToken?: string;
    };
  };
}

interface UseGetLocationsListParams {
  pageSize?: number;
  pageToken?: string | null;
  pageNumber?: number;
  method?: 'token' | 'pageNumber' | 'firstPage';
  companyId?: string;
}

export interface UpdateLocationTurbinesRequest {
  name: string;
  category: string;
  turbines: Array<{
    id: string;
  }>;
}
export const useGetLocationsList = (
  params: UseGetLocationsListParams = {},
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  const {
    pageSize = 5,
    pageToken,
    pageNumber,
    method = 'firstPage',
    companyId
  } = params;

  return useQuery<LocationsResponse>({
    queryKey: locationsKey.listWithPagination(
      token,
      pageSize,
      pageToken,
      pageNumber,
      method,
      companyId
    ),
    queryFn: () => {
      return getLocationsV3Serv(token, {
        pageSize,
        pageToken,
        pageNumber,
        method,
        companyId
      }) as Promise<LocationsResponse>;
    },
    enabled: Boolean(token),
    ...useQueryOptions
  });
};

const transformFormDataToApiPayload = (
  formData: LocationFormData
): LocationApiPayload => {
  return {
    name: formData.name,
    address: formData.address,
    isActive: formData.status === 'Active' ? true : false,
    startDate: getUTC(formData.startDate).dateUTC,
    customer: formData.customer,
    type: 'power',
    category: 'powersite',
    city: formData.nearestCity,
    state: formData.state,
    geoReference: {
      latitude: formData.latitude,
      longitude: formData.longitude
    },
    status: formData.status,
    dataPointVisibility: Object.entries(formData.dataPoints)
      .filter(([, value]) => value)
      .map(([key]) => key)
  };
};

export const useCreateUpdateLocation = (mode: 'create' | 'edit') => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: LocationFormData) => {
      const apiPayload = transformFormDataToApiPayload(formData);

      if (mode === 'edit' && formData.id) {
        return updatelocationServ(token, apiPayload, formData.id);
      } else {
        return createLocations(token, apiPayload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationsKey.all });
    },
    onError: error => {
      console.error('Error saving location:', error);
    }
  });
};

export const useGetLocationDetails = (
  locationId: string,
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: locationsKey.detail(token, locationId),
    queryFn: () => {
      return getLocationIdServ(token, locationId);
    },
    enabled: Boolean(token && locationId),
    ...useQueryOptions
  });
};

export const useGetLocationAccessLevels = (
  locationId: string,
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: locationsKey.accessLevels(token, locationId),
    queryFn: () => {
      return getLocationAccessLevelsServ(token, locationId);
    },
    enabled: Boolean(token && locationId),
    ...useQueryOptions
  });
};

export const useGetUnassignedTurbines = ({ useQueryOptions = {} } = {}) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: locationsKey.unassignedTurbines(token),
    queryFn: () => {
      return getUnassignedTurbinesServ(token);
    },
    enabled: Boolean(token),
    meta: { errorDisplay: 'card' as const },
    ...useQueryOptions
  });
};

export const useGetUpdateLocation = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);

  return useMutation({
    mutationFn: async ({
      data,
      id,
      isUnassign = false
    }: {
      data: UpdateLocationTurbinesRequest;
      id: string;
      isUnassign?: boolean;
    }) => {
      return updatelocationServ(token, data, id, isUnassign);
    },
    onSuccess: () => {},
    onError: error => {
      console.error('Error updating location turbines:', error);
    }
  });
};

export const useDeleteUserCompany = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);

  return useMutation({
    mutationFn: async ({
      id,
      locationId
    }: {
      id: string;
      locationId: string;
    }) => {
      return deleteUserCompanyServ(token, id, locationId);
    },
    onSuccess: () => {},
    onError: error => {
      console.error('Error updating location turbines:', error);
    }
  });
};

export interface UpdateLocationAccessLevelsRequest {
  companies?: Array<{
    id: string;
    name: string;
  }>;
  users?: Array<{
    name: string;
    username: string;
  }>;
}

/**
 * Hook to update location access levels (assign companies and/or users)
 * @returns {UseMutationResult} Mutation result for updating access levels
 */
export const useUpdateLocationAccessLevels = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      locationId,
      data
    }: {
      locationId: string;
      data: Required<UpdateLocationAccessLevelsRequest>;
    }) => {
      const safeData = {
        companies: data.companies ?? [],
        users: data.users ?? []
      };
      return updateLocationAccessLevelsServ(token, locationId, safeData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: locationsKey.accessLevels(token, variables.locationId)
      });
    },
    onError: error => {
      console.error('Error updating location access levels:', error);
    }
  });
};

export interface PowerSite {
  id: string;
  name: string;
  geoCoordinates?: {
    latitude: string;
    longitude: string;
  };
  city?: string;
  state?: string;
  genTotalMW: number;
  unit: string;
  turbines?: string;
  turbinesCount?: number;
}

export interface PowerSitesResponse {
  status: string;
  data: PowerSite[];
  info?: any;
  meta?: any;
}

export const useGetPowerSites = ({ useQueryOptions = {} } = {}) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery<PowerSitesResponse>({
    queryKey: locationsKey.powerSites(token),
    queryFn: () => {
      return getPowerSitesServ(token) as Promise<PowerSitesResponse>;
    },
    enabled: Boolean(token),
    meta: { errorDisplay: 'toast' as const },
    ...useQueryOptions
  });
};

export interface InstalledProductMeasures {
  ngpAnalog: number;
  pcntLoadAnalog: number;
  t5AverageTemperatureAnalog: number;
}

export interface InstalledProductGenTotal {
  min: number;
  max: number;
  description: string;
  label: string;
  value: number;
}

export interface InstalledProduct {
  id: string;
  type: string;
  status: 'ok' | 'warning' | 'shutdown' | 'offline';
  measures: InstalledProductMeasures;
  genTotalKwAnalog: InstalledProductGenTotal;
}

export interface InstalledProductsData {
  power: InstalledProduct[];
  genTotalMW?: number;
}

export interface InstalledProductsMeta {
  class: string;
  version: string;
  sentAt: string;
}

export interface InstalledProductsResponse {
  status: string;
  _meta: InstalledProductsMeta;
  data: InstalledProductsData;
  info?: any;
  meta?: any;
}

export const useGetInstalledProducts = (
  siteId: string,
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  const summaryParams = { view: INSTALLED_PRODUCTS_VIEW_SUMMARY };

  return useQuery<InstalledProductsResponse>({
    queryKey: [...locationsKey.installedProducts(token, siteId), summaryParams],
    queryFn: async () => {
      const data = await getInstalledProductsBySiteServ(
        token,
        siteId,
        summaryParams
      );
      return data as InstalledProductsResponse;
    },
    enabled: Boolean(token && siteId),
    meta: { errorDisplay: 'toast' as const },
    ...useQueryOptions
  });
};

export interface DataPointItem {
  key: string;
  label: string;
}

export interface DataPointsResponse {
  status: string;
  data: DataPointItem[] | Record<string, string>;
  info?: any;
  meta?: any;
}

const transformDataPointsToLabels = (
  apiData: DataPointItem[] | Record<string, string>
): Record<string, string> | null => {
  if (!apiData) {
    return null;
  }

  if (Array.isArray(apiData)) {
    return apiData.reduce(
      (acc, item) => {
        acc[item.key] = item.label;
        return acc;
      },
      {} as Record<string, string>
    );
  }

  return apiData as Record<string, string>;
};

export const useGetDataPoints = ({ useQueryOptions = {} } = {}) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: locationsKey.dataPoints(token),
    queryFn: async () => {
      const data = await getDataPointsServ(token);
      return data as DataPointsResponse;
    },
    enabled: Boolean(token),
    select: (data: DataPointsResponse) => {
      const labels = transformDataPointsToLabels(data.data);
      return {
        ...data,
        labels
      };
    },
    ...useQueryOptions
  });
};

export interface LocationWithAccessLevels {
  id: string;
  name: string;
  status: string;
  turbines?: Array<{
    id: string;
    powerStatus?: string;
  }>;
  category?: string;
  city?: string;
  state?: string;
  customer?: string;
  accessLevels?: {
    companies?: Array<{
      id: string;
      name: string;
    }>;
    users?: Array<any>;
  };
}

export interface LocationsWithAccessLevelsResponse {
  status: string;
  data: LocationWithAccessLevels[];
  info?: any;
  _meta?: any;
}

export const useGetLocationsWithAccessLevels = (
  username: string,
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery<LocationsWithAccessLevelsResponse>({
    queryKey: locationsKey.locationsWithAccessLevels(token, username),
    queryFn: async () => {
      const data = await getLocationsWithAccessLevelsServ(token, username);
      return {
        status: typeof data.status === 'string' ? data.status : 'success',
        data: data.data || [],
        info: data.info,
        _meta: data.info
      } as LocationsWithAccessLevelsResponse;
    },
    enabled: Boolean(token && username),
    ...useQueryOptions
  });
};

export const useDeleteLocation = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      return deletelocationServ(token, id);
    },
    onSuccess: () => {},
    onError: error => {
      console.error('Error updating location turbines:', error);
    }
  });
};

export { useGetLocationsList as default };
